let engine;

export async function createEngineInstance() {
  if (engine) return engine;

  const { Liquid } = await import('liquidjs');

  engine = new Liquid();

  engine.registerFilter('currency_format', function (money, format) {
    function defaultOption(opt, def) {
      return typeof opt == 'undefined' ? def : opt;
    }

    const replaceDotCommaZero = (str) => {
      // replace end string ,00 or .00 -> empty
      return str?.replace(/[.,]00$/, '');
    };

    function formatWithDelimiters(number, precision, thousands, decimal) {
      if (number !== 0 && !number) return '';

      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      number = parseFloat(`${number}`).toFixed(precision);

      const parts = replaceDotCommaZero(number.toString()).split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const money = parts[1] ? decimal + parts[1] : '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';
      const moneyFormatted = formatString
        .replace(/&#36;/g, '$')
        .replace(placeholderRegex, dollars + money);

      return moneyFormatted;
    }

    switch (format) {
      case 'amount':
        return formatWithDelimiters(money, 2);
      case 'amount_no_decimals':
        return formatWithDelimiters(money, 0);
      case 'amount_with_comma_separator':
        return formatWithDelimiters(money, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatWithDelimiters(money, 0, '.', ',');
      case 'amount_with_space_separator_no_comma':
        return formatWithDelimiters(money, 2);
      case 'amount_no_decimals_with_space_separator':
        return formatWithDelimiters(money, 0, ' ', '.');
      default:
        return formatWithDelimiters(money, 2);
    }
  });

  engine.registerFilter('translate', translate);

  function productMetafield(product, metafieldInfo) {
    if (typeof product !== 'object') return '';
    if (!product.metafields || product?.metafields.length === 0) return '';
    const [namespace, key] = metafieldInfo.split('.');
    const metafield = product.metafields.find(
      (metafield) =>
        String(metafield.namespace) === String(namespace) && String(metafield.key) === String(key)
    );

    if (metafield) return metafield.value;

    return '';
  }

  function compare(str1, str2) {
    str1 = String(str1);
    str2 = String(str2);
    return str1 === str2;
  }

  engine.registerFilter('json_parse', jsonParse);
  engine.registerFilter('compare', compare);

  engine.registerFilter('pagination_list', function (page, totalPage) {
    return paginationListFunction(page, totalPage).join(',');
  });

  engine.registerFilter('product_metafield', productMetafield);
  engine.registerFilter('format_currency', formatCurrency);

  return engine;
}

export function translate(key, translations, defaultLabel) {
  if (!translations || typeof translations !== 'object' || !key) {
    return defaultLabel;
  }
  const keys = key.split('.');
  let translation = translations;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(translation, k)) {
      translation = translation[k];
    } else {
      return defaultLabel;
    }
  }
  if (typeof translation !== 'string') return defaultLabel;
  return translation !== undefined ? translation : defaultLabel;
}

export const paginationListFunction = (page, totalPage) => {
  if (totalPage < 6) {
    return [...Array(totalPage).keys()].map((i) => i + 1);
  }

  if (page < 5) {
    // 0 present for ...
    return [1, 2, 3, 4, 5, 0, totalPage];
  }

  if (totalPage - page < 4) {
    return [1, 0, totalPage - 4, totalPage - 3, totalPage - 2, totalPage - 1, totalPage];
  }

  return [1, 0, page - 1, page, page + 1, 0, totalPage];
};

function jsonParse(str) {
  try {
    return JSON.parse(str);
  } catch (error) {
    console.log(error);
    return str;
  }
}

export const templateRender = (templateString, data) => {
  try {
    if (typeof templateString === 'string') {
      const template = engine.parseAndRenderSync(templateString, {
        ...data,
        data,
      });
      return template;
    } else {
      const template = engine.renderSync(templateString, {
        ...data,
        data,
      });
      return template;
    }
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getTypeFormatMoney = (format) => {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';
  const matches = formatString.match(placeholderRegex);
  const match = matches && matches.length > 1 ? matches[1] : '';

  return match;
};

export const formatMoneyByType = (type, money) => {
  function defaultOption(opt, def) {
    return typeof opt == 'undefined' ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (number !== 0 && !number) return '';

    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    number = parseFloat(`${number}`).toFixed(precision);

    const parts = number.toString().split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const money = parts[1] ? decimal + parts[1] : '';

    return dollars + money;
  }

  switch (type) {
    case 'amount':
      return formatWithDelimiters(money, 2);
    case 'amount_no_decimals':
      return formatWithDelimiters(money, 0);
    case 'amount_with_comma_separator':
      return formatWithDelimiters(money, 2, '.', ',');
    case 'amount_no_decimals_with_comma_separator':
      return formatWithDelimiters(money, 0, '.', ',');
    case 'amount_with_space_separator_no_comma':
      return formatWithDelimiters(money, 2);
    case 'amount_no_decimals_with_space_separator':
      return formatWithDelimiters(money, 0, ' ', '.');
    default:
      return formatWithDelimiters(money, 2);
  }
};

export const compileMoneyWithConfigFormat = (moneyFormat, format) => {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  let formatString = format || '${{amount}}';

  formatString = formatString.replace(/&#36;/g, '$').replace(placeholderRegex, moneyFormat);

  return formatString;
};

export const formatMoney = (
  money,
  format,
  withTrailingZeros,
  showCentAsSuperscript,
  removePriceDecimal,
  removeDecimalPoint,
  decimalDelimiter
) => {
  if (typeof money === 'string') {
    money = money.replace('.', '');
  }

  // Get type format money
  const typeFormatMoney = getTypeFormatMoney(format);

  // Format money by type
  let moneyFormat = formatMoneyByType(typeFormatMoney, money);

  // Format money with trailing zeros
  if (!withTrailingZeros) moneyFormat = moneyFormat.replace(/((,00)|(\.00))$/g, '');

  // Remove decimal
  if (removePriceDecimal) {
    moneyFormat = removeDecimal(moneyFormat, decimalDelimiter);
  }

  // Format money with cent as superscript
  if (showCentAsSuperscript) {
    const [ints, cents] = separateDecimalMoney(moneyFormat, typeFormatMoney, removeDecimalPoint);
    if (cents) moneyFormat = `${ints}<sup>${cents}</sup>`;
  }

  // Replace config format with money format
  return compileMoneyWithConfigFormat(moneyFormat, format);
};

export const getDecimalPoint = (format) => {
  switch (format) {
    case 'amount':
    case 'amount_with_space_separator_no_comma':
      return '.';
    case 'amount_with_comma_separator':
      return ',';
    case 'amount_no_decimals':
    case 'amount_no_decimals_with_comma_separator':
    case 'amount_no_decimals_with_space_separator':
    default:
      return null;
  }
};

export const separateDecimalMoney = (moneyFormat, format, removeDecimalPoint) => {
  const decimalPoint = getDecimalPoint(format);
  if (!decimalPoint) return [`${moneyFormat}`];

  const [ints, cents] = `${moneyFormat}`.split(decimalPoint);

  return cents ? [ints, removeDecimalPoint ? `${cents}` : `${decimalPoint}${cents}`] : [ints];
};

export const removeDecimal = (value, delimiter) => {
  const reg = new RegExp('(\\' + delimiter + '\\d+)+', 'gi');
  return ('' + value).replace(reg, '');
};

export const stripHtml = (string) => {
  return string?.toString()?.replace(/<.*?>/gi, '').replace(/&#36;/g, '$');
};

export const formatCurrency = (
  money,
  format,
  showCentAsSuperscript,
  removePriceDecimal,
  decimalDelimiter,
  withoutTrailingZeros
) => {
  // Get type format money
  const typeFormatMoney = getTypeFormatMoney(format);

  // Format money by type
  let moneyFormat = formatMoneyByType(typeFormatMoney, money);

  // Handle trailing zeros
  if (withoutTrailingZeros) {
    moneyFormat = moneyFormat.replace(/((,00)|(\.00))$/g, '');
  }

  // Remove decimal
  if (removePriceDecimal) {
    moneyFormat = removeDecimal(moneyFormat, decimalDelimiter);
  }

  // Format money with cent as superscript
  if (showCentAsSuperscript) {
    const [ints, cents] = separateDecimalMoney(moneyFormat, typeFormatMoney, showCentAsSuperscript);
    if (cents) moneyFormat = `${ints}<sup>${cents}</sup>`;
  }

  // Replace config format with money format
  return compileMoneyWithConfigFormat(moneyFormat, format);
};

export const templateParse = async function (templateString) {
  const engine = await createEngineInstance();

  return engine.parse(templateString);
};
